/**
 * Client-side allowlist sanitizer for the one place this app renders HTML it
 * did not build itself: a gear item's detail body.
 *
 * This duplicates backend/server/showcase/richTextSanitizer.js on purpose. The
 * backend pass is authoritative — it decides what may be *stored*. This one
 * decides what may be *rendered*, and it exists precisely so the answer doesn't
 * depend on the database being trustworthy: a row edited by hand, restored from
 * an old backup, or written by some future endpoint that forgot to sanitize
 * still cannot put script into the page. Neither pass is load-bearing alone.
 *
 * Everywhere else in the frontend, values go through Component.escape (#106).
 * This module is the single, deliberate exception, which is why it parses into
 * an inert document rather than touching innerHTML on a live one: nothing here
 * ever executes, loads, or navigates while being inspected.
 *
 * Keep the three constants below in lockstep with the backend's.
 */

const ALLOWED = {
  P: [],
  BR: [],
  STRONG: [],
  EM: [],
  U: [],
  S: [],
  H2: [],
  H3: [],
  H4: [],
  UL: [],
  OL: [],
  LI: [],
  BLOCKQUOTE: [],
  CODE: [],
  A: ["href"],
  SPAN: ["class"],
};

// Elements whose content goes with them — their bodies are code, not prose.
const DROP_WITH_CONTENT = new Set([
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "FORM",
  "INPUT",
  "TEMPLATE",
  "SVG",
  "MATH",
  "NOSCRIPT",
]);

// Presentational tags that mean exactly what an allowlisted tag means, mapped
// to it instead of being unwrapped. document.execCommand emits this older
// vocabulary — <b>, <i>, <strike> — so without this, bold/italic/strikethrough
// would look applied in the editor and quietly vanish on the way to storage.
// Pasted content from other editors carries the same tags.
const TAG_ALIASES = {
  B: "strong",
  I: "em",
  STRIKE: "s",
  DEL: "s",
  INS: "u",
};

export const ALLOWED_CLASSES = ["rt-size-sm", "rt-size-lg", "rt-size-xl"];
const ALLOWED_CLASS_SET = new Set(ALLOWED_CLASSES);
const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);

/** The href to keep, or null to drop it (keeping the link text). */
export function safeHref(value) {
  try {
    const url = new URL(String(value).trim());
    return ALLOWED_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * An element's allowlist key. `tagName` is uppercase for HTML elements but
 * keeps its source case for foreign ones — the `<script>` inside an `<svg>`
 * reports "script", not "SCRIPT" — so it has to be normalized before any
 * lookup, or the drop lists silently miss exactly the elements they exist for.
 */
function tagKey(el) {
  return el.tagName.toUpperCase();
}

/** Strip everything not allowed from one element, in place. */
function scrubElement(el) {
  const allowedAttrs = ALLOWED[tagKey(el)];
  for (const attr of [...el.attributes]) {
    if (!allowedAttrs.includes(attr.name)) el.removeAttribute(attr.name);
  }

  if (tagKey(el) === "A") {
    const href = safeHref(el.getAttribute("href") || "");
    if (href) {
      el.setAttribute("href", href);
      // Set here, never carried over from the input.
      el.setAttribute("rel", "noopener noreferrer nofollow");
      el.setAttribute("target", "_blank");
    } else {
      el.removeAttribute("href");
    }
  }

  if (tagKey(el) === "SPAN") {
    const kept = (el.getAttribute("class") || "").split(/\s+/).filter((name) => ALLOWED_CLASS_SET.has(name));
    if (kept.length) el.setAttribute("class", kept.join(" "));
    else el.removeAttribute("class");
  }
}

/**
 * Swap an element for one with the same children under a different tag name.
 * Attributes are deliberately not carried over — the aliased tags have none
 * this sanitizer would keep anyway.
 */
function retag(element, tagName) {
  const replacement = element.ownerDocument.createElement(tagName);
  replacement.append(...element.childNodes);
  element.replaceWith(replacement);
  return replacement;
}

/** Recursively clean a parsed subtree, in place. */
function scrubTree(root) {
  // Snapshot the children first: unwrapping replaces nodes mid-walk, and a
  // live childNodes list would skip whatever moved up into the gap.
  for (const node of [...root.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE) continue;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove(); // comments, CDATA, processing instructions
      continue;
    }
    if (DROP_WITH_CONTENT.has(tagKey(node))) {
      node.remove();
      continue;
    }
    const alias = TAG_ALIASES[tagKey(node)];
    const element = alias ? retag(node, alias) : node;
    scrubTree(element);
    if (tagKey(element) in ALLOWED) {
      scrubElement(element);
    } else {
      element.replaceWith(...element.childNodes); // unwrap: keep the children
    }
  }
}

/**
 * Sanitize an HTML fragment down to the allowlist. Returns a string; safe to
 * call on anything, including null and garbage.
 */
export function sanitizeHtml(html) {
  if (html == null) return "";
  // DOMParser builds an inert document: no scripts run, no images or
  // stylesheets are fetched, nothing navigates.
  const doc = new DOMParser().parseFromString(`<body>${String(html)}</body>`, "text/html");
  scrubTree(doc.body);
  return doc.body.innerHTML;
}

/**
 * Replace `el`'s contents with sanitized `html`. Nodes are moved across from
 * the inert parse rather than assigned through innerHTML, so the markup is
 * never re-parsed in a live document after being checked.
 */
export function renderInto(el, html) {
  if (!el) return;
  el.replaceChildren();
  if (html == null) return;
  const doc = new DOMParser().parseFromString(`<body>${String(html)}</body>`, "text/html");
  scrubTree(doc.body);
  el.append(...doc.body.childNodes);
}

/** Whether a sanitized fragment holds any actual text (vs. e.g. "<p><br></p>"). */
export function hasContent(html) {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(`<body>${String(html)}</body>`, "text/html");
  return Boolean(doc.body.textContent.trim());
}

export default sanitizeHtml;
